import React, { useEffect, useState } from "react";
import { Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Web Push enable/disable button (Iter-171).
 *
 * Three states:
 *   • "default"  — user hasn't decided. Renders "Enable notifications" button.
 *   • "granted"  — subscribed. Renders "Notifications enabled" with disable action.
 *   • "denied"   — user blocked browser permission. Renders an info chip (cannot recover from JS).
 *
 * Hidden if the browser doesn't support PushManager + Notification API.
 */

function urlBase64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export default function EnablePushButton({ className = "", testid = "enable-push-btn" }) {
  const [perm, setPerm] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const supported = typeof window !== "undefined" && "Notification" in window && "serviceWorker" in navigator && "PushManager" in window;

  useEffect(() => {
    if (!supported) return;
    setPerm(Notification.permission);
    navigator.serviceWorker.ready.then((reg) => reg.pushManager.getSubscription()).then((sub) => {
      setSubscribed(!!sub);
    }).catch(() => {});
  }, [supported]);

  if (!supported) return null;

  const enable = async () => {
    setBusy(true);
    try {
      const result = await Notification.requestPermission();
      setPerm(result);
      if (result !== "granted") {
        toast.error("Notifications denied — you can re-enable from the browser site settings.");
        return;
      }
      const r = await api.get("/push/vapid-public-key");
      const key = r.data.public_key;
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(key),
        });
      }
      const json = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: json.endpoint,
        keys: json.keys,
      });
      setSubscribed(true);
      toast.success("Notifications enabled — you'll get push alerts on this device.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Could not enable notifications");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        try { await sub.unsubscribe(); } catch { /* ignore */ }
        try { await api.post("/push/unsubscribe", { endpoint }); } catch { /* ignore */ }
      }
      setSubscribed(false);
      toast.info("Notifications disabled on this device.");
    } catch (e) {
      toast.error(e?.message || "Error");
    } finally {
      setBusy(false);
    }
  };

  if (perm === "denied") {
    return (
      <div
        data-testid={`${testid}-denied`}
        className={`text-[11px] text-red-700 px-3 py-2 rounded-md bg-red-50 border border-red-200 leading-snug flex items-start gap-2 ${className}`}
      >
        <BellOff size={14} className="shrink-0 mt-0.5" />
        <span>Notifications blocked. Open the browser site settings to allow them again.</span>
      </div>
    );
  }

  if (subscribed) {
    return (
      <button
        type="button"
        onClick={disable}
        disabled={busy}
        data-testid={`${testid}-disable`}
        className={`relative w-full flex items-center justify-center gap-2 px-3 h-10 bg-emerald-50 border border-emerald-300 text-emerald-800 text-sm rounded-md font-medium hover:bg-emerald-100 disabled:opacity-50 ${className}`}
      >
        <Bell size={14} /> Notifications enabled (tap to disable)
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={enable}
      disabled={busy}
      data-testid={testid}
      className={`relative w-full flex items-center justify-center gap-2 px-3 h-10 bg-brand-navy text-white text-sm rounded-md font-medium hover:bg-brand-navy-600 disabled:opacity-50 ${className}`}
    >
      <Bell size={14} /> {busy ? "Enabling…" : "Enable notifications"}
    </button>
  );
}
